/**
 * Reading a key (surfaces.md §3.1). One function, pure, used by every answer
 * surface, so the three layouts get one rule:
 *
 *   1. `event.repeat` on Enter is ignored and logged (`op: repeat`);
 *   2. the character, if it means something in this slot, is used as typed;
 *   3. otherwise a `Digit0`–`Digit9` / `Numpad0`–`Numpad9` key is read by its
 *      code (`op: ins_code`, the produced key kept);
 *   4. otherwise the key is refused: nothing on screen, `op: refuse` in the trace.
 *
 * Why not „digits always by code": a US `Shift+9` is `(`, an interval bracket,
 * and a US `Shift+6` is `^`, the exponent. Why not „always by character": the
 * Windows Lithuanian layout turns `Digit4` into `ė`. Character first when it
 * means something, then code, is right on all three.
 */

/** The parts of a KeyboardEvent the reader looks at, so tests need no DOM. */
export interface KeyLike {
  key: string;
  code: string;
  shiftKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  repeat?: boolean;
}

/** The digit a physical key carries, whatever the layout printed on it. */
export function digitOfCode(code: string): string | null {
  const m = /^(?:Digit|Numpad)([0-9])$/.exec(code);
  return m ? m[1] : null;
}

/** A key that produces one character (as opposed to Enter, Tab, Shift, F5…). */
export function isPrintable(k: KeyLike): boolean {
  return [...k.key].length === 1;
}

/** Keys that are not keystrokes at all and never reach the trace: a modifier
 *  held on its own, a lock, a function key. */
export function isInert(k: KeyLike): boolean {
  return (
    ["Shift", "Control", "Alt", "AltGraph", "Meta", "CapsLock", "NumLock", "ScrollLock", "OS", "Fn", "ContextMenu"].includes(
      k.key,
    ) || /^F[0-9]{1,2}$/.test(k.key)
  );
}

/** A browser shortcut (copy, paste, select all, reload) passes through untouched;
 *  a paste then arrives as its own event and is logged as one `paste` entry. */
export function isBrowserChord(k: KeyLike): boolean {
  return !!(k.ctrlKey || k.metaKey) && k.key !== "Enter";
}

export type Reading =
  | { kind: "char"; ch: string } // step 2: used by character
  | { kind: "code"; ch: string } // step 3: a digit by code
  | { kind: "refuse"; why: string }; // step 4

/**
 * Steps 2–4 for one printable key, given what the slot accepts. `meaningful`
 * answers „does this character mean something here", which is the slot's own
 * business: a digit, a separator the slot can leave by, a letter it takes.
 */
export function readChar(k: KeyLike, meaningful: (ch: string) => boolean): Reading {
  if (k.key === "Dead") {
    const d = digitOfCode(k.code);
    return d !== null ? { kind: "code", ch: d } : { kind: "refuse", why: "dead" };
  }
  if (isPrintable(k) && meaningful(k.key)) return { kind: "char", ch: k.key };
  const d = digitOfCode(k.code);
  if (d !== null) return { kind: "code", ch: d };
  return { kind: "refuse", why: "not_in_slot" };
}
